import React, { forwardRef } from 'react';
import { CircleLoader } from 'components/CircleLoader/CircleLoader';
import './Button.scss';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger' | 'success';
export type ButtonSize = 'small' | 'medium' | 'large';
export type ButtonShape = 'rounded' | 'circle' | 'square';

export type ButtonProps = {
  children?: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  icon?: React.ComponentType<{ size?: number; className?: string; fill?: string }>;
  iconPosition?: 'left' | 'right';
  isLoading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'medium',
      shape = 'rounded',
      icon: Icon,
      iconPosition = 'left',
      isLoading = false,
      disabled = false,
      fullWidth = false,
      className = '',
      type = 'button',
      onClick,
      ...props
    },
    ref
  ) => {
    const buttonClasses = [
      'ui-button',
      `ui-button--${variant}`,
      `ui-button--${size}`,
      `ui-button--${shape}`,
      fullWidth && 'ui-button--full-width',
      isLoading && 'ui-button--loading',
      disabled && 'ui-button--disabled',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const iconSize = size === 'small' ? 16 : size === 'large' ? 24 : 20;

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isLoading || disabled) {
        e.preventDefault();
        return;
      }
      onClick?.(e);
    };

    const renderIcon = () => {
      if (isLoading) {
        return (
          <span className="ui-button__loader">
            <CircleLoader isActive={true} size="xx-small" />
          </span>
        );
      }

      if (Icon) {
        return (
          <span className={`ui-button__icon ui-button__icon--${iconPosition}`}>
            <Icon size={iconSize} className="ui-button__icon-svg" />
          </span>
        );
      }

      return null;
    };

    return (
      <button
        ref={ref}
        type={type}
        className={buttonClasses}
        onClick={handleClick}
        disabled={disabled || isLoading}
        {...props}
      >
        {iconPosition === 'left' && renderIcon()}
        {children && <span className="ui-button__text">{children}</span>}
        {iconPosition === 'right' && renderIcon()}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
